import { supabase } from '@/integrations/supabase/client';

/**
 * Tiny cache with in-flight de-duplication + sessionStorage persistence.
 * Several pages/components request the same heavy RPCs at the same time on
 * first paint — this collapses them into a single network round-trip, and
 * revisiting a page paints instantly from the persisted snapshot while the
 * fresh data loads in the background.
 */
const inflight = new Map<string, Promise<any>>();
const cache = new Map<string, { at: number; value: any }>();
const DEFAULT_TTL = 60_000;
const STORE_PREFIX = 'isc_sr_';
/** Keys that should survive a full page reload / new tab (localStorage). */
const PERSIST_LOCAL = new Set<string>();

function store(key: string): Storage | null {
  try {
    return PERSIST_LOCAL.has(key) ? localStorage : sessionStorage;
  } catch {
    return null;
  }
}

function readPersisted(key: string): { at: number; value: any } | null {
  try {
    const raw = store(key)?.getItem(STORE_PREFIX + key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writePersisted(key: string, entry: { at: number; value: any }) {
  try {
    const raw = JSON.stringify(entry);
    // Don't blow up the 5MB storage quota with huge payloads.
    if (raw.length > 1_500_000) return;
    store(key)?.setItem(STORE_PREFIX + key, raw);
  } catch {
    /* quota / private mode — memory cache still works */
  }
}


export async function sharedRequest<T>(
  key: string,
  loader: () => Promise<T>,
  ttl = DEFAULT_TTL,
  /** Serve the cached value immediately and refresh it in the background. */
  staleWhileRevalidate = false
): Promise<T> {
  let hit = cache.get(key);
  if (!hit) {
    const persisted = readPersisted(key);
    if (persisted) {
      cache.set(key, persisted);
      hit = persisted;
    }
  }
  if (hit && Date.now() - hit.at < ttl) return hit.value as T;

  const running = inflight.get(key);
  if (running) {
    if (hit && staleWhileRevalidate) return hit.value as T;
    return running as Promise<T>;
  }

  const p = loader()
    .then((value) => {
      const entry = { at: Date.now(), value };
      cache.set(key, entry);
      writePersisted(key, entry);
      return value;
    })
    .catch((err) => {
      // Offline / transient failure: keep serving the last good snapshot.
      if (hit) return hit.value as T;
      throw err;
    })
    .finally(() => inflight.delete(key));
  inflight.set(key, p);

  // Stale data available → paint now, refresh silently.
  if (hit && staleWhileRevalidate) {
    p.catch(() => {});
    return hit.value as T;
  }
  return p;
}

/** Drop cached responses (call after a mutation that changes them). */
export function clearSharedCache(keyPrefix?: string) {
  const removeStored = (k: string) => {
    try { sessionStorage.removeItem(STORE_PREFIX + k); } catch { /* ignore */ }
    try { localStorage.removeItem(STORE_PREFIX + k); } catch { /* ignore */ }
  };
  if (!keyPrefix) {
    cache.clear();
    try {
      [...Object.keys(sessionStorage), ...Object.keys(localStorage)]
        .filter((k) => k.startsWith(STORE_PREFIX))
        .forEach((k) => { try { sessionStorage.removeItem(k); } catch {} try { localStorage.removeItem(k); } catch {} });
    } catch { /* ignore */ }
    return;
  }
  for (const k of Array.from(cache.keys())) {
    if (k.startsWith(keyPrefix)) {
      cache.delete(k);
      removeStored(k);
    }
  }
  // Also purge persisted keys that were never loaded into memory this session.
  try {
    [...Object.keys(sessionStorage), ...Object.keys(localStorage)]
      .filter((k) => k.startsWith(STORE_PREFIX + keyPrefix))
      .forEach((k) => { try { sessionStorage.removeItem(k); } catch {} try { localStorage.removeItem(k); } catch {} });
  } catch { /* ignore */ }
}


/**
 * Fetch all rows from an RPC function, bypassing the 1000-row response cap.
 * The first page is fetched alone; if it is full, the remaining pages are
 * fetched in parallel batches instead of one slow sequential chain.
 */
/** Heavy, slow-changing RPCs get a longer cache + instant stale paint. */
const RPC_CACHE_POLICY: Record<string, { ttl: number; persist: boolean }> = {
  get_leaderboard: { ttl: 10 * 60_000, persist: true },
  get_leaderboard_detailed: { ttl: 10 * 60_000, persist: true },
  get_public_profiles: { ttl: 15 * 60_000, persist: true },
};

export async function fetchAllRpc<T = any>(
  fnName: string,
  pageSize = 1000
): Promise<T[]> {
  const key = `rpc:${fnName}:${pageSize}`;
  const policy = RPC_CACHE_POLICY[fnName];
  if (policy?.persist) PERSIST_LOCAL.add(key);
  return sharedRequest(
    key,
    async () => {
      const page = async (from: number): Promise<T[]> => {
        const { data, error } = await (supabase.rpc as any)(fnName).range(from, from + pageSize - 1);
        if (error || !data) return [];
        return data as T[];
      };

      const first = await page(0);
      if (first.length < pageSize) return first;

      const all: T[] = [...first];
      let from = pageSize;
      const CONCURRENCY = 4;
      // Fetch 4 pages at a time until a batch comes back short (= end reached).
      for (;;) {
        const batch = await Promise.all(
          Array.from({ length: CONCURRENCY }, (_, i) => page(from + i * pageSize))
        );
        batch.forEach((rows) => all.push(...rows));
        const done = batch.some((rows) => rows.length < pageSize);
        if (done) break;
        from += CONCURRENCY * pageSize;
      }
      return all;
    },
    policy?.ttl ?? DEFAULT_TTL,
    !!policy,
  );
}


/** Public profile map, shared across pages (dashboard, batch, results, subjects). */
export async function fetchPublicProfiles<T = any>(): Promise<T[]> {
  return sharedRequest('rpc:get_public_profiles', async () => {
    const { data } = await (supabase.rpc as any)('get_public_profiles').limit(10000);
    return (data as T[]) || [];
  });
}

/** How long a cached visible-tests snapshot is considered fresh. */
const TESTS_META_TTL = 5 * 60_000;
const TESTS_META_PREFIX = 'rpc:get_visible_tests_meta';

async function testsMetaKey(): Promise<string> {
  try {
    const { data } = await supabase.auth.getSession();
    return `${TESTS_META_PREFIX}:${data.session?.user?.id ?? 'anon'}`;
  } catch {
    return `${TESTS_META_PREFIX}:anon`;
  }
}

/**
 * Visible test metadata, shared across dashboard/subject tests/batch/coach.
 * Cached per user in localStorage so a page reload (or a second tab) paints
 * from the snapshot instantly and only revalidates in the background.
 */
export async function fetchVisibleTestsMeta<T = any>(): Promise<T[]> {
  const key = await testsMetaKey();
  PERSIST_LOCAL.add(key);
  return sharedRequest(
    key,
    async () => {
      const { data, error } = await (supabase.rpc as any)('get_visible_tests_meta');
      if (error) throw error;
      return (data as T[]) || [];
    },
    TESTS_META_TTL,
    true
  );
}

/** Force the next leaderboard/profile RPC calls to hit the network. */
export function invalidateLeaderboards() {
  clearSharedCache('rpc:get_leaderboard');
  clearSharedCache('rpc:get_leaderboard_detailed');
  clearSharedCache('rpc:get_public_profiles');
}

/** Force the next fetchVisibleTestsMeta() call to hit the network. */
export function invalidateVisibleTestsMeta() {
  clearSharedCache(TESTS_META_PREFIX);
}


/**
 * The signed-in user's re-attempt pass — deduped across components on the same page.
 * Pass `force` after a payment submit / approval so the fresh status is fetched.
 */
export async function fetchMyRetryAccess<T = any>(userId: string, force = false): Promise<T[]> {
  const key = `rpc:get_my_retry_access:${userId}`;
  if (force) clearSharedCache(key);
  return sharedRequest(key, async () => {
    const { data, error } = await (supabase.rpc as any)('get_my_retry_access');
    if (error) throw error;
    return (data as T[]) || [];
  }, 60_000, !force);
}

/** Next + upcoming scheduled tests in one cached round-trip (instant repeat paint). */
export async function fetchScheduledTestsMeta(): Promise<{ next: any[]; upcoming: any[] }> {
  PERSIST_LOCAL.add('rpc:scheduled_tests');
  return sharedRequest('rpc:scheduled_tests', async () => {
    const [n, u] = await Promise.all([
      supabase.rpc('get_next_scheduled_test' as any),
      supabase.rpc('get_upcoming_scheduled_tests' as any),
    ]);
    return { next: (n.data as any[]) || [], upcoming: (u.data as any[]) || [] };
  }, 60_000, true);
}

/** The signed-in user's own profile row, shared by sidebar/dialogs/presence. */
export async function fetchMyProfile(userId: string): Promise<any | null> {
  return sharedRequest(`profile:${userId}`, async () => {
    const { data } = await supabase
      .from('profiles')
      .select('avatar_url, display_name, full_name, district, phone, reg_id')
      .eq('user_id', userId)
      .maybeSingle();
    return data ?? null;
  });
}
